import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'
import {
  canManageContractFinanceFlag,
  defaultNotifyFlagsForRole,
  isManagerRole,
  getRoleOptions,
  type UserItem,
} from '@/components/Configuracao/usersTabConstants'
import { userSetorOptions } from '@/components/Configuracao/userSetorConstants'
import { canEnableEventModeForRole } from '@/utils/userEventMode'
import { useAuthStore } from '@/stores/authStore'

interface UserModalProps {
  user?: UserItem | null
  onClose: () => void
  onSaved: () => void
}

export function UserModal({ user, onClose, onSaved }: UserModalProps) {
  const viewerRole = useAuthStore((s) => s.user?.role)
  const canManageFinanceFlag = canManageContractFinanceFlag(viewerRole)
  const [name, setName] = useState(user?.name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [role, setRole] = useState(user?.role?.toUpperCase() ?? 'BDR')
  const [setor, setSetor] = useState(user?.setor?.toUpperCase() ?? '')
  const [notifyNewFinanceProject, setNotifyNewFinanceProject] = useState(
    user?.notifyNewFinanceProject ?? defaultNotifyFlagsForRole(role).notifyNewFinanceProject,
  )
  const [notifyFinanceInbox, setNotifyFinanceInbox] = useState(
    user?.notifyFinanceInbox ?? defaultNotifyFlagsForRole(role).notifyFinanceInbox,
  )
  const [canViewContractFinance, setCanViewContractFinance] = useState(
    user?.canViewContractFinance ?? false,
  )
  const [eventModeActive, setEventModeActive] = useState(user?.eventModeActive ?? false)
  const [password, setPassword] = useState('')
  const [sendPasswordSetupEmail, setSendPasswordSetupEmail] = useState(true)
  const [saving, setSaving] = useState(false)
  const isEdit = !!user

  useEffect(() => {
    if (!isEdit) {
      const defaults = defaultNotifyFlagsForRole(role)
      setNotifyNewFinanceProject(defaults.notifyNewFinanceProject)
      setNotifyFinanceInbox(defaults.notifyFinanceInbox)
    }
    if (!isManagerRole(role)) {
      setCanViewContractFinance(false)
    }
    if (!canEnableEventModeForRole(role)) {
      setEventModeActive(false)
    }
  }, [role, isEdit])

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Nome e e-mail são obrigatórios')
      return
    }
    if (!isEdit && !sendPasswordSetupEmail && password.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres')
      return
    }

    const payload = {
      name,
      email,
      role,
      setor: setor || null,
      notifyNewFinanceProject,
      notifyFinanceInbox,
      ...(canManageFinanceFlag
        ? { canViewContractFinance: isManagerRole(role) ? canViewContractFinance : false }
        : {}),
      ...(canEnableEventModeForRole(role) ? { eventModeActive } : {}),
      ...(isEdit ? {} : { sendPasswordSetupEmail }),
    }

    setSaving(true)
    try {
      if (isEdit) {
        await api.patch(`/users/${user!.id}`, payload)
        toast.success('Usuário atualizado')
      } else {
        const res = await api.post('/users', {
          ...payload,
          ...(sendPasswordSetupEmail ? {} : { password }),
        })
        const emailSent = Boolean(res.data?.data?.passwordSetupEmailSent)
        toast.success(
          emailSent
            ? 'Usuário criado. E-mail enviado para definição de senha.'
            : sendPasswordSetupEmail
              ? 'Usuário criado, mas o e-mail não pôde ser enviado. Reenvie pelo menu de senha.'
              : 'Usuário criado',
        )
      }
      onSaved()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar usuário'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const showGestorEmail = ['MANAGER', 'ADMIN'].includes(role)
  const showFinanceEmail = ['FINANCEIRO', 'ADMIN'].includes(role)
  const showEventMode = canEnableEventModeForRole(role)

  const fieldClass =
    'w-full min-h-[44px] rounded-md border border-[var(--border)] bg-[var(--bg-main)] px-3 py-2 text-xs text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]'

  const sectionClass =
    'space-y-2 rounded-lg border border-[var(--border)] bg-[var(--bg-main)] p-3'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[min(90vh,720px)] overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">
            {isEdit ? 'Editar Usuário' : 'Novo Usuário'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              Nome
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome completo"
              className={fieldClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              E-mail
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@empresa.com"
              type="email"
              className={fieldClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              Cargo
            </label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={fieldClass}>
              {getRoleOptions().map((o) => (
                <option key={o.value} value={o.value} className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              Setor
            </label>
            <select value={setor} onChange={(e) => setSetor(e.target.value)} className={fieldClass}>
              <option value="" className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                Não definido
              </option>
              {userSetorOptions.map((o) => (
                <option key={o.value} value={o.value} className="bg-[var(--bg-card)] text-[var(--text-primary)]">
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-[11px] leading-relaxed text-[var(--text-muted)]">
              Área de atuação do colaborador (independente do cargo). Usado para direcionar demandas e
              visibilidade operacional.
            </p>
          </div>

          {showEventMode && (
            <div className="space-y-2 rounded-lg border border-amber-500/35 bg-amber-500/10 p-3">
              <label className="flex cursor-pointer items-start gap-2 text-xs text-[var(--text-primary)]">
                <input
                  type="checkbox"
                  checked={eventModeActive}
                  onChange={(e) => setEventModeActive(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <strong>Modo evento</strong> (comercial)
                  <span className="mt-1 block leading-relaxed text-[var(--text-muted)]">
                    Enquanto ativo, o usuário não acessa Empresas e Contatos — ideal para captura de
                    leads em eventos sem expor a base.
                    {user?.eventModeActivatedAt && eventModeActive && (
                      <span className="mt-1 block">
                        Ativado em {new Date(user.eventModeActivatedAt).toLocaleString('pt-BR')}
                      </span>
                    )}
                  </span>
                </span>
              </label>
            </div>
          )}

          {(showGestorEmail || showFinanceEmail || isManagerRole(role)) && (
            <div className={sectionClass}>
              <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
                Financeiro
              </p>
              {showGestorEmail && (
                <label className="flex cursor-pointer items-start gap-2 text-xs text-[var(--text-primary)]">
                  <input
                    type="checkbox"
                    checked={notifyNewFinanceProject}
                    onChange={(e) => setNotifyNewFinanceProject(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Recebe e-mail de <strong>novo projeto</strong> (gestor / gerente)
                  </span>
                </label>
              )}
              {showFinanceEmail && (
                <label className="flex cursor-pointer items-start gap-2 text-xs text-[var(--text-primary)]">
                  <input
                    type="checkbox"
                    checked={notifyFinanceInbox}
                    onChange={(e) => setNotifyFinanceInbox(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Recebe notificações da <strong>fila financeira</strong> (Janice / time)
                  </span>
                </label>
              )}
              {canManageFinanceFlag && isManagerRole(role) && (
                <label className="flex cursor-pointer items-start gap-2 text-xs text-[var(--text-primary)]">
                  <input
                    type="checkbox"
                    checked={canViewContractFinance}
                    onChange={(e) => setCanViewContractFinance(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Ver <strong>valores financeiros</strong> de contrato (Finance / Gestores / CS)
                  </span>
                </label>
              )}
            </div>
          )}

          {!isEdit && (
            <div className={sectionClass}>
              <label className="flex cursor-pointer items-start gap-2 text-xs text-[var(--text-primary)]">
                <input
                  type="checkbox"
                  checked={sendPasswordSetupEmail}
                  onChange={(e) => {
                    setSendPasswordSetupEmail(e.target.checked)
                    if (e.target.checked) setPassword('')
                  }}
                  className="mt-0.5"
                />
                <span>
                  <strong>Enviar e-mail</strong> para o usuário definir a própria senha
                  <span className="mt-1 block text-[var(--text-muted)]">
                    Recomendado. O link expira em 72 horas.
                  </span>
                </span>
              </label>

              {!sendPasswordSetupEmail && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
                    Senha
                  </label>
                  <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    type="password"
                    className={fieldClass}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-accent px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Usuário'}
          </button>
        </div>
      </div>
    </div>
  )
}
